import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { adminApi, setUnauthorizedHandler } from '@/api/client'
import { queryClient } from '@/lib/query-client'
import {
  AdminLoginResponseSchema,
  AdminMeResponseSchema,
  type AdminLoginResponse,
  type AdminUser
} from '@novelaide/shared-types/admin-auth'
import {
  clearSession,
  loadAdmin,
  loadToken,
  saveAdmin,
  saveToken
} from './storage'

interface AuthState {
  admin: AdminUser | null
  token: string | null
}

interface AuthContextValue extends AuthState {
  isAuthenticated: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => void
}

// Better Auth session token 是 opaque credential，客户端不解析内容。
// Gateway 在每次 /admin/* 请求中校验 session；这里仅恢复同 tab 的 UI 状态。
function initialState(): AuthState {
  const token = loadToken()
  const admin = loadAdmin()
  if (token && admin) {
    return { admin, token }
  }
  if (token || admin) clearSession()
  return { admin: null, token: null }
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(initialState)

  const logout = useCallback(() => {
    clearSession()
    queryClient.clear()
    setState({ admin: null, token: null })
  }, [])

  const login = useCallback(async (username: string, password: string) => {
    const res = await adminApi.post<AdminLoginResponse>(
      '/admin/login',
      { username, password },
      AdminLoginResponseSchema,
      {
        skipAuth: true,
        fallbackErrorCode: 'login_failed'
      }
    )
    saveToken(res.token)
    saveAdmin(res.admin)
    queryClient.clear()
    setState(initialState())
  }, [])

  // 注册 401 全局兜底:被 client.ts 调用 → 走 logout 流程
  useEffect(() => {
    setUnauthorizedHandler(logout)
    return () => setUnauthorizedHandler(() => {})
  }, [logout])

  // 恢复缓存 UI 后后台复核服务端 session。401 由 adminApi 的全局处理器退出；
  // 临时网络错误不删除仍可能有效的 session，后续请求会继续由 Gateway 校验。
  useEffect(() => {
    if (!state.token || !state.admin) return
    const restoredToken = state.token
    const restoredAdminId = state.admin.id
    let canceled = false

    void adminApi
      .get('/admin/me', AdminMeResponseSchema)
      .then((me) => {
        if (canceled || loadToken() !== restoredToken) return
        if (me.adminId !== restoredAdminId || !me.username) logout()
      })
      .catch(() => {
        // 401 已触发 logout；其它错误保留 session，避免瞬时网络故障强制重登。
      })

    return () => {
      canceled = true
    }
  }, [state.admin, state.token, logout])

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      isAuthenticated: state.token !== null && state.admin !== null,
      login,
      logout
    }),
    [state, login, logout]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
